import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { IconEdit, IconPlus, IconTrash, IconWorld } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAmplitudeTrack } from 'amplitude/useAmplitude';
import { useAtom, useAtomValue } from 'jotai';
import isEqual from 'lodash/isEqual';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  Dropdown,
  useBreakpoints,
  useMediaQuery,
} from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { withRoles } from '@/utils/withRoles';

import StickyMenuButton from '@/components/Cards/StickyMenuCard/StickyMenuButton';
import StickyMenuCard from '@/components/Cards/StickyMenuCard/StickyMenuCard';
import { Header } from '@/components/header';
import type { ManageTranslationsState } from '@/components/popups/ManageTranslationsModal/state';

import { queryClient } from '@/services/api';
import type { CustomError } from '@/types/types';

import GenericError from 'components/Error/GenericError';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay';
import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import ConfirmationModal from 'components/popups/ConfirmationModal';
import ManageTranslationsPopup from 'components/popups/ManageTranslationsModal/ManageTranslations';
import ToastMessageBlock from 'components/Toasts/SuccessToast';
import Constants, { routes } from 'constants/index';
import { getNameFromLanguage, itemByLanguage } from 'utils/language';
import { isApiError } from 'utils/request';
import { validateTextField } from 'utils/validateTextField';

import BuildQuizzesQuestionCard from './BuildQuizzesItems/BuildQuizzesQuestionCard';
import DeleteQuizPopup from './BuildQuizzesItems/DeleteQuizPopup';
import RenameQuizPopup from './BuildQuizzesItems/RenameQuizPopup';
import ValidationErrorPopup from './BuildQuizzesItems/ValidationErrorPopup';

import {
  createCustomQuiz,
  CustomQuizCreateRequest,
  CustomQuizDetailDefinitionDto,
  deleteCustomQuiz,
  getCustomQuizDetailsOptions,
  getCustomQuizDetailsQueryKey,
  getCustomQuizzesQueryKey,
  updateCustomQuiz,
} from '@/_generated/api-clients/xp';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import {
  getPendingSaveFlagSync,
  quizDraftAtom,
  setPendingSaveFlagSync,
} from '@/atoms/quiz-draft';

interface BuildQuizzesLocationStateProps {
  state?: {
    operator?: {
      id: string;
    };
    quizName: string;
  };
}

const DEFAULT_QUIZ = {
  operatorId: '',
  name: {
    en: '',
  },
  passingGrade: undefined,
  questions: [Constants.DEFAULT_MULTIPLE_CHOICE_QUESTION],
  formId: '',
  quizId: '',
} satisfies CustomQuizDetailDefinitionDto;

const BuildQuizzesView = () => {
  const [storedQuiz, setStoredQuiz] = useAtom(quizDraftAtom);
  const { quizId } = useParams<{ quizId: string }>();
  const { t } = useTranslation();
  const { data: user } = useAuth();
  const isLicenseeUser = user?.isLicensee;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const userIsStaff = user?.isStaff;
  const userId = user?.userId;
  const breakpoints = useBreakpoints();
  const isSmAndDown = useMediaQuery(breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation() as BuildQuizzesLocationStateProps;

  const track = useAmplitudeTrack();

  const userLanguage = user?.language ?? 'en';

  const [validationMode, setValidationMode] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [quiz, setQuiz] = useState<CustomQuizDetailDefinitionDto>({
    ...DEFAULT_QUIZ,
  });
  const [showSaveBeforeRenaming, setShowSaveBeforeRenaming] = useState(false);
  const [showSaveBeforeTranslating, setShowSaveBeforeTranslating] =
    useState(false);
  const [showAddToPlanPopup, setShowAddToPlanPopup] = useState(false);
  const [showConfirmClosePopUp, setShowConfirmClosePopUp] = useState(false);
  const [showDeleteQuizPopup, setShowDeleteQuizPopup] = useState(false);
  const [showRenameQuizPopup, setShowRenameQuizPopup] = useState(false);
  const [showManageTranslationsPopup, setShowManageTranslationsPopup] =
    useState(false);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);

  const [header, setHeader] = useState('');

  // Track if we've initialized quiz state to avoid infinite loop with draft saving
  // Using ref instead of state because we don't need re-renders when this changes
  const hasInitializedQuizRef = useRef(false);

  const isEditMode = !!quizId;

  const invalidateQuizzes = async (id?: string) =>
    id
      ? await Promise.all([
          queryClient.refetchQueries({ queryKey: getCustomQuizzesQueryKey() }),
          queryClient.refetchQueries({
            queryKey: getCustomQuizDetailsQueryKey({
              path: {
                customQuizId: id,
              },
            }),
          }),
        ])
      : await queryClient.refetchQueries({
          queryKey: getCustomQuizzesQueryKey(),
        });

  const addQuiz = async (
    body: CustomQuizCreateRequest,
    addToPlan: boolean,
    onSuccess?: () => void,
  ) => {
    try {
      // Mark that a save is in progress SYNCHRONOUSLY (must survive Okta redirect)
      // Using sync localStorage write because Jotai's async update may not complete before redirect
      setPendingSaveFlagSync(true);

      const { data, error } = await createCustomQuiz({ body });

      // Check for API error (the client doesn't throw by default)
      // NOTE: Don't clear pendingSave on error - an auth error (500) may trigger
      // an Okta redirect, and we want the flag to survive so the user is warned
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        return;
      }

      // Clear pendingSave flag only on success
      setPendingSaveFlagSync(false);
      setStoredQuiz(null);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${getNameFromLanguage(quiz.name)} ${t(
            'TrainingPlans.toastMessage.created',
          )}`}
        </ToastMessageBlock>
      ));
      if (track !== null) {
        track('quiz_created', {
          quiz_id: data.id,
        });
      }
      await invalidateQuizzes();
      onSuccess?.();

      if (addToPlan) {
        navigate({
          pathname: routes.plans.managedPlans,
        });
      } else {
        navigate(routes.plans.managedQuizzes);
      }
    } catch (error) {
      // NOTE: Don't clear pendingSave on error - the error might precede an Okta redirect
      if (isApiError(error as CustomError | Error)) {
        datadogRum.addError(error);
        return <GenericError />;
      }
      datadogRum.addError(error);
    }
  };

  const updateQuiz = async (
    body: CustomQuizDetailDefinitionDto,
    onSuccess?: () => void,
  ) => {
    try {
      if (!body.quizId)
        throw new Error('Quiz ID is required for updating a quiz.');

      // Mark that a save is in progress SYNCHRONOUSLY (must survive Okta redirect)
      // Using sync localStorage write because Jotai's async update may not complete before redirect
      setPendingSaveFlagSync(true);

      const { error } = await updateCustomQuiz({ body });

      // Check for API error (the client doesn't throw by default)
      // NOTE: Don't clear pendingSave on error - an auth error (500) may trigger
      // an Okta redirect, and we want the flag to survive so the user is warned
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        return;
      }

      // Clear pendingSave flag only on success
      setPendingSaveFlagSync(false);
      await invalidateQuizzes(body.quizId);
      onSuccess?.();
    } catch (error) {
      // NOTE: Don't clear pendingSave on error - the error might precede an Okta redirect
      datadogRum.addError(error);
    }
  };

  const deleteQuiz = async (id: string, onSuccess?: () => void) => {
    try {
      const { error } = await deleteCustomQuiz({ path: { customQuizId: id } });

      // Check for API error (the client doesn't throw by default)
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorDeleting')}
          </ToastMessageBlock>
        ));
        return;
      }

      await invalidateQuizzes();
      onSuccess?.();
    } catch (error) {
      datadogRum.addError(error);
    }
  };

  const {
    data: quizData,
    isFetching: isFetchingQuiz,
    isPending: isPendingQuiz,
  } = useQuery({
    ...getCustomQuizDetailsOptions({
      path: {
        customQuizId: quizId ?? '',
      },
    }),
    enabled: isEditMode,
  });

  const isFetching = isPendingQuiz && isEditMode;

  // Derived state: calculate isEdited directly instead of using useEffect
  // React Compiler will auto-memoize this computation
  const isEdited = quizData
    ? !isEqual(
        {
          ...quiz,
          passingGrade: Number(quiz.passingGrade),
        },
        quizData,
      )
    : false;

  const prepareQuizOrNavigate = useEffectEvent(() => {
    // Only run initialization logic once - skip if already initialized
    if (hasInitializedQuizRef.current) {
      return;
    }

    // Scenario 1: We have a stored draft that matches our current context
    // For new quiz: storedQuiz exists and has no existingQuizId (or null)
    // For edit quiz: storedQuiz exists and existingQuizId matches URL quizId
    const isDraftForCurrentContext =
      storedQuiz &&
      (isEditMode
        ? storedQuiz.existingQuizId === quizId
        : !storedQuiz.existingQuizId);

    if (isDraftForCurrentContext) {
      // Restore from draft (handles browser refresh and Okta redirect)
      setHeader(itemByLanguage(storedQuiz.name, ''));
      setQuiz(storedQuiz);
      hasInitializedQuizRef.current = true;

      // If a save was in progress when redirected, warn the user
      if (storedQuiz.pendingSave) {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        // Clear the flag in both localStorage (sync) and Jotai state
        setPendingSaveFlagSync(false);
        setStoredQuiz({ ...storedQuiz, pendingSave: false });
      }
    } else if (
      location.state?.quizName &&
      (location.state.operator?.id || userIsStaff)
    ) {
      // Scenario 2: Fresh navigation with location.state (new quiz from popup)
      const { quizName, operator } = location.state;
      setHeader(quizName);
      setQuiz(prevState => ({
        ...prevState,
        operatorId: operator?.id ?? userId ?? '',
        name: { en: quizName },
      }));
      hasInitializedQuizRef.current = true;
    } else if (isEditMode) {
      // Scenario 3: Edit mode - load from API
      if (quizData) {
        setHeader(itemByLanguage(quizData.name, ''));
        setQuiz(quizData);
        hasInitializedQuizRef.current = true;
      } else if (!isFetchingQuiz) {
        navigate(routes.plans.managedQuizzes);
      }
    } else {
      // Scenario 4: No valid context - redirect back
      navigate(routes.plans.managedQuizzes);
    }
  });

  useEffect(() => {
    prepareQuizOrNavigate();
  }, [quizData]);

  const saveDraft = useEffectEvent(() => {
    // Skip saving until we've finished initializing the quiz state
    // This prevents an infinite loop: quiz changes -> save to storedQuiz -> storedQuiz changes -> restore quiz
    if (!hasInitializedQuizRef.current) {
      return;
    }

    // Save draft for BOTH new quizzes and edit sessions
    // This ensures state survives browser refresh and Okta redirect
    // NOTE: Read pendingSave directly from localStorage to avoid race conditions with Jotai's async state
    setStoredQuiz({
      ...quiz,
      questions: [...quiz.questions],
      existingQuizId: isEditMode ? quizId : null,
      savedAt: Date.now(),
      pendingSave: getPendingSaveFlagSync(),
    });
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveDraft();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [quiz]);

  const isValidQuizName = (quizName: string, operator: string) => {
    if (!quizName || !operator) {
      setShowInvalidNameError(true);
      console.error('invalid quiz name or missing operator');
      datadogRum.addError(
        new Error(
          `Build quizzes error - no quiz name or no operator: ${JSON.stringify({
            quizName,
            operator,
          })}`,
        ),
      );
      return false;
    }
    return true;
  };

  const addQuestion = () => {
    setValidationMode(false);
    handlePassingGrade();
    setQuiz(prevState => ({
      ...prevState,
      questions: [
        ...prevState.questions,
        ...[Constants.DEFAULT_MULTIPLE_CHOICE_QUESTION],
      ],
    }));
  };

  const validateQuiz = () => {
    let isValid = true;
    isValid = quiz.passingGrade !== undefined;
    quiz.questions.forEach(question => {
      isValid =
        isValid && validateTextField(question.question[userLanguage] ?? '');
      isValid =
        isValid && question.answers.some(option => option.correct === true);
      question.answers.forEach(answer => {
        isValid =
          isValid && validateTextField(answer.answer[userLanguage] ?? '');
      });
    });
    return isValid;
  };

  const onConfirmClose = () => {
    setStoredQuiz(null);
    setShowConfirmClosePopUp(false);
    navigate({
      pathname: routes.plans.managedQuizzes,
    });
  };

  const onSaveQuiz = () => {
    setValidationMode(true);
    const isValid = validateQuiz();
    if (!isValid) {
      setShowValidationError(true);
      return;
    } else if (isEditMode) {
      editQuiz();
    } else {
      setShowAddToPlanPopup(true);
    }
  };

  const editQuiz = () => {
    const isValid = validateQuiz();
    if (!isValid) {
      setShowValidationError(true);
      return;
    }
    // this checks if the english questions and answers are empty because the backend
    // does not accept empty english strings, and fills them with the spanish strings
    const parsedQuestions = quiz.questions.map(question => ({
      ...question,
      question: {
        ...question.question,
        en:
          question.question.en === '' && question.question.es
            ? question.question.es
            : (question.question.en ?? ''),
      },
      answers: question.answers.map(answer => ({
        ...answer,
        answer: {
          ...answer.answer,
          en:
            answer.answer.en === '' && answer.answer.es
              ? answer.answer.es
              : (answer.answer.en ?? ''),
        },
      })),
    }));

    updateQuiz(
      {
        ...quiz,
        questions: parsedQuestions,
        passingGrade: Number(quiz.passingGrade),
      },
      () => {
        // Clear the draft after successful update
        setStoredQuiz(null);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {`${getNameFromLanguage(quiz.name)} ${t(
              'TrainingPlans.toastMessage.updated',
            )}`}
          </ToastMessageBlock>
        ));
        // fire amplitude event
        if (track !== null) {
          track('quiz_updated', {
            quiz_id: quizId ?? 'empty',
          });
        }
        navigate(routes.plans.managedQuizzes);
      },
    );
  };

  const createQuiz = (addToPlan: boolean) => {
    setShowAddToPlanPopup(false);
    // this checks if the english questions and answers are empty because the backend
    // does not accept empty english strings, and fills them with the spanish strings
    const parsedQuestions = quiz.questions.map(question => ({
      ...question,
      question: {
        ...question.question,
        en:
          question.question.en === '' && question.question.es
            ? question.question.es
            : (question.question.en ?? ''),
      },
      answers: question.answers.map(answer => ({
        ...answer,
        answer: {
          ...answer.answer,
          en:
            answer.answer.en === '' && answer.answer.es
              ? answer.answer.es
              : (answer.answer.en ?? ''),
        },
      })),
    }));

    addQuiz(
      {
        ...quiz,
        location: isLicenseeUser ? activeLicenseeLocation?.number : undefined,
        questions: parsedQuestions,
        passingGrade: Number(quiz.passingGrade),
      },
      addToPlan,
    );
  };

  const onSaveTranslations = (updatedTranslations: ManageTranslationsState) => {
    setShowManageTranslationsPopup(false);
    updateQuiz(updatedTranslations as CustomQuizDetailDefinitionDto, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${itemByLanguage(updatedTranslations.name)} ${t(
            'TrainingPlans.translationToastText',
          )} ${
            userLanguage === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE
              ? Constants.LANGUAGE_OPTIONS.SPANISH
              : Constants.LANGUAGE_OPTIONS.INGLES
          }`}
        </ToastMessageBlock>
      ));
    });
  };

  const onRenameQuiz = (newName: string) => {
    const trimmedName = newName.trim();
    if (!isValidQuizName(trimmedName, quiz.operatorId)) {
      return;
    }
    const payload: CustomQuizDetailDefinitionDto = {
      ...quiz,
      name: {
        ...quiz.name,
        [userLanguage]: trimmedName,
      },
    };

    updateQuiz(payload, () => {
      setHeader(trimmedName);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${trimmedName} ${t('TrainingPlans.toastMessage.renamed')}`}
        </ToastMessageBlock>
      ));
    });
  };

  const onDeleteQuiz = (id: string, quizName: string) => {
    deleteQuiz(id, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>{`${quizName} ${t(
          'TrainingPlans.toastMessage.deleted',
        )}`}</ToastMessageBlock>
      ));
      navigate({
        pathname: routes.plans.managedQuizzes,
      });
    });
  };

  const handlePassingGrade = (value?: number) => {
    setQuiz(prevState => ({
      ...prevState,
      passingGrade: value,
    }));
  };

  const onShowRenameQuizPopup = () => {
    if (isEdited) {
      setShowSaveBeforeRenaming(true);
    } else {
      setShowRenameQuizPopup(true);
    }
  };

  const onDeleteQuizAndClosePopup = () => {
    onDeleteQuiz(quiz.quizId, itemByLanguage(quiz.name, ''));
    setShowDeleteQuizPopup(false);
  };

  const onRenameQuizAndClosePopup = (inputValue: string) => {
    onRenameQuiz(inputValue);
    setShowRenameQuizPopup(false);
  };

  const onShowDeleteQuizPopup = () => {
    setShowDeleteQuizPopup(true);
  };

  const onShowManageTranslationsPopup = () => {
    if (isEdited) {
      setShowSaveBeforeTranslating(true);
    } else {
      setShowManageTranslationsPopup(true);
    }
  };

  return (
    <>
      <Header.Root>
        <Header.Back
          to={routes.plans.managedQuizzes}
          tx="TrainingPlans.tabManageQuizzes"
        />
        <Header.Title tx="Generic.editQuiz" />
      </Header.Root>
      <LoadingOverlay isOpen={isFetching} fixed />
      {!isFetching && (
        <div className="md:mt-header-height-with-content-spacing centered-content px-4 max-md:mt-6">
          <div className="mb-6 flex items-center justify-between gap-4">
            <p className="text-variant-h3 truncate text-left">{header}</p>
            <PopoverMenuButton className="md:hidden">
              <PopoverMenuButtonItem
                dataTestId="ThreeDotMenuQuizPopupRename"
                icon={<IconEdit />}
                onClick={onShowRenameQuizPopup}
                text={t('TrainingPlans.manageThreeDotMenu.renameQuiz')}
              />
              <PopoverMenuButtonItem
                dataTestId="ThreeDotMenuManageTranslationsPopup"
                icon={<IconWorld />}
                onClick={onShowManageTranslationsPopup}
                text={t('Generic.editTranslations')}
              />
              <PopoverMenuButtonItem
                dataTestId="deleteQuizMenuItem"
                icon={<IconTrash />}
                isDestructive
                onClick={onShowDeleteQuizPopup}
                text={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
              />
            </PopoverMenuButton>
          </div>
          <div className="flex flex-col gap-6 md:flex-row">
            <div className="size-full">
              {quiz.questions.map((_, questionIndex) => (
                <BuildQuizzesQuestionCard
                  handlePassingGrade={handlePassingGrade}
                  isEditMode={isEditMode}
                  key={`questionCard-${questionIndex}`}
                  questionIndex={questionIndex}
                  quiz={quiz}
                  setQuiz={setQuiz}
                  setValidationMode={setValidationMode}
                  validationMode={validationMode}
                />
              ))}
              <Button
                className="mb-4"
                color="secondary"
                onClick={addQuestion}
                size="lg"
                variant="text"
              >
                <IconPlus />
                {t('Button.addQuestion')}
              </Button>
              <Card elevation={1} variant="outlined">
                <CardContent>
                  <div className="mt-2 mb-4 [&_.cfa-dropdown]:max-w-[256px]">
                    <Dropdown
                      data-testid="QuizQuestionDropdown"
                      errorText={
                        !quiz.passingGrade && validationMode
                          ? t(
                              'TrainingPlans.buildQuizzes.errorSelectPassingGrade',
                            )
                          : ''
                      }
                      fullWidth
                      label={t(
                        'TrainingPlans.buildQuizzes.correctQuestionsThreshold',
                      )}
                      onChange={newVal => handlePassingGrade(Number(newVal))}
                      options={[
                        '0',
                        ...quiz.questions.map((_, index) =>
                          (index + 1).toString(),
                        ),
                      ]}
                      placeholder={t('Button.select')}
                      required
                      value={quiz.passingGrade?.toString()}
                    />
                  </div>
                </CardContent>
              </Card>
              <div className="mt-4 mb-12 flex flex-col justify-center md:mx-0 md:mt-8 md:flex-row">
                <Button
                  className="mt-3.5! min-w-[unset]! md:mx-2!"
                  color="secondary"
                  onClick={() => {
                    setShowConfirmClosePopUp(true);
                  }}
                  size="md"
                  variant="outlined"
                >
                  {t('Button.cancel')}
                </Button>
                <Button
                  className="mt-3.5! min-w-[unset]! md:mx-2!"
                  color="secondary"
                  data-testid="SaveQuiz"
                  onClick={onSaveQuiz}
                  size="md"
                  variant="filled"
                >
                  {t('Button.saveQuiz')}
                </Button>
              </div>
              <ConfirmationModal
                bodyText={t('Generic.cancelConfirmation')}
                headerText={t('Generic.unsavedChanges')}
                isOpen={showConfirmClosePopUp}
                onClose={() => setShowConfirmClosePopUp(false)}
                primaryButtonHandler={onConfirmClose}
                primaryButtonText={t('Button.discardChanges')}
                primaryButtonVariant="destructive"
                secondaryButtonHandler={() => setShowConfirmClosePopUp(false)}
                secondaryButtonText={t('Button.continueEditing')}
              />
              <ConfirmationModal
                bodyText={t('TrainingPlans.buildQuizzes.addToPlan')}
                headerText={t('TrainingPlans.buildQuizzes.addToPlanHeader')}
                isOpen={showAddToPlanPopup}
                onClose={() => setShowAddToPlanPopup(false)}
                primaryButtonHandler={() => createQuiz(true)}
                primaryButtonText={t('Button.choosePlan')}
                secondaryButtonHandler={() => createQuiz(false)}
                secondaryButtonText={t('Button.notNow')}
              />
              <ValidationErrorPopup
                setShowValidationError={setShowValidationError}
                showValidationError={showValidationError}
              />
              <DeleteQuizPopup
                onDeleteQuizAndClosePopup={onDeleteQuizAndClosePopup}
                quizName={getNameFromLanguage(quiz.name)}
                setShowDeleteQuizPopup={setShowDeleteQuizPopup}
                showDeleteQuizPopup={showDeleteQuizPopup}
              />
              <RenameQuizPopup
                onRenameQuizAndClosePopup={onRenameQuizAndClosePopup}
                quizDetails={quiz}
                setShowRenameQuizPopup={setShowRenameQuizPopup}
                showRenameQuizPopup={showRenameQuizPopup}
              />
              <ManageTranslationsPopup
                isOpen={showManageTranslationsPopup}
                onClose={() => setShowManageTranslationsPopup(false)}
                onSave={onSaveTranslations}
                translatableObject={quiz}
              />

              <ConfirmationModal
                bodyText={t(
                  'TrainingPlans.buildQuizzes.saveBeforeRenamingQuiz',
                )}
                headerText={t('Generic.unsavedChanges')}
                isOpen={showSaveBeforeRenaming}
                onClose={() => setShowSaveBeforeRenaming(false)}
                primaryButtonHandler={() => setShowSaveBeforeRenaming(false)}
                primaryButtonText={t('Button.goBack')}
              />
              <ConfirmationModal
                bodyText={t(
                  'TrainingPlans.buildQuizzes.saveBeforeTranslatingQuiz',
                )}
                headerText={t('Generic.unsavedChanges')}
                isOpen={showSaveBeforeTranslating}
                onClose={() => setShowSaveBeforeTranslating(false)}
                primaryButtonHandler={() => setShowSaveBeforeTranslating(false)}
                primaryButtonText={t('Button.goBack')}
              />
            </div>
            <ConfirmationModal
              bodyText={t('InvalidQuizName.paragraphText')}
              headerText={t('InvalidQuizName.errorHeader')}
              isOpen={showInvalidNameError}
              onClose={() => setShowInvalidNameError(false)}
              primaryButtonHandler={() => setShowInvalidNameError(false)}
              primaryButtonText={t('Button.close')}
            />
            {!isSmAndDown && isEditMode && (
              <StickyMenuCard
                header={t('TrainingPlans.buildQuizzes.manageQuiz')}
                className="md:top-[calc(var(--header-height)+16px)]!"
              >
                <StickyMenuButton
                  dataTestId="ThreeDotMenuQuizPopupRename"
                  icon={<IconEdit />}
                  onClick={onShowRenameQuizPopup}
                  text={t('TrainingPlans.manageThreeDotMenu.renameQuiz')}
                />
                <StickyMenuButton
                  dataTestId="ThreeDotMenuManageTranslationsPopup"
                  icon={<IconWorld />}
                  onClick={onShowManageTranslationsPopup}
                  text={t('Generic.editTranslations')}
                />
                <StickyMenuButton
                  dataTestId="deleteQuizMenuItem"
                  error
                  icon={<IconTrash />}
                  onClick={onShowDeleteQuizPopup}
                  text={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
                />
              </StickyMenuCard>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const BuildQuizzesViewWithRoles = withRoles(BuildQuizzesView, 'quizzes.create');

export default BuildQuizzesViewWithRoles;
